import { mulberry32 } from '@halvard/rules';

/**
 * Where the scenery around the lake goes, from the lake's seed: every lake gets its own shore.
 * World units: the board spans -4.5…4.5 on x and z; Brand's camp is at +z, Rime's at -z.
 */

export interface Floe {
  x: number;
  z: number;
  /** Width and depth. */
  w: number;
  d: number;
  angle: number;
  /** Phase of its slow bobbing, so floes don't move in step. */
  phase: number;
}

export interface Mound {
  x: number;
  z: number;
  /** Radius on the ground and height. */
  radius: number;
  height: number;
}

export interface Pine {
  x: number;
  z: number;
  height: number;
}

export interface Surroundings {
  floes: Floe[];
  banks: Mound[];
  rocks: Mound[];
  pines: Pine[];
}

/** Open water from the board's edge out to here; snow beyond. */
export const SHORE_RADIUS = 9;
/** No scenery closer to the board than this (Chebyshev distance from the centre). */
export const BOARD_MARGIN = 5.1;
/** The camps: centred behind each side's back rank. */
// Brand's longship is frozen in the lake, far enough back to stay out of Brand's own view.
export const BRAND_CAMP = { x: 0, z: 8 } as const;
// Rime lives on the shore: a longhouse in the snow, past the open water.
export const RIME_CAMP = { x: 0, z: -(SHORE_RADIUS + 0.9) } as const;
const CAMP_HALF = { x: 2.4, z: 1.3 } as const;

/** Whether a point (with some size) would sit on the board or in a camp. */
export function blocked(x: number, z: number, size: number): boolean {
  if (Math.max(Math.abs(x), Math.abs(z)) - size < BOARD_MARGIN) return true;
  for (const camp of [BRAND_CAMP, RIME_CAMP]) {
    if (Math.abs(x - camp.x) < CAMP_HALF.x + size && Math.abs(z - camp.z) < CAMP_HALF.z + size) {
      return true;
    }
  }
  return false;
}

/**
 * Places `count` things in a ring between two radii, skipping blocked spots. With `apart`,
 * things never overlap each other either.
 */
function scatter<T>(
  rng: () => number,
  count: number,
  inner: number,
  outer: number,
  make: (x: number, z: number) => { item: T; size: number },
  apart = false,
): T[] {
  const out: T[] = [];
  const placed: { x: number; z: number; size: number }[] = [];
  for (let tries = 0; out.length < count && tries < count * 30; tries++) {
    const angle = rng() * Math.PI * 2;
    const radius = inner + (outer - inner) * Math.sqrt(rng());
    const x = Math.cos(angle) * radius;
    const z = Math.sin(angle) * radius;
    const { item, size } = make(x, z);
    if (blocked(x, z, size)) continue;
    if (apart && placed.some((p) => Math.hypot(p.x - x, p.z - z) < (p.size + size) * 1.15))
      continue;
    placed.push({ x, z, size });
    out.push(item);
  }
  return out;
}

export function layoutSurroundings(seed: number): Surroundings {
  const rng = mulberry32((seed ^ 0x51ed270b) >>> 0);
  const floes = scatter(
    rng,
    20,
    5.6,
    SHORE_RADIUS - 0.6,
    (x, z) => {
      const w = 0.3 + rng() * 0.5;
      const d = 0.25 + rng() * 0.35;
      return {
        item: { x, z, w, d, angle: rng() * Math.PI, phase: rng() * Math.PI * 2 },
        size: Math.hypot(w, d) / 2,
      };
    },
    true,
  );
  // Snowbanks along the shoreline break up its circle.
  const banks = scatter(rng, 34, SHORE_RADIUS - 0.4, SHORE_RADIUS + 2.5, (x, z) => {
    const radius = 0.45 + rng() * 0.6;
    return { item: { x, z, radius, height: 0.18 + rng() * 0.27 }, size: radius };
  });
  const rocks = scatter(rng, 12, SHORE_RADIUS - 0.2, SHORE_RADIUS + 3, (x, z) => {
    const radius = 0.15 + rng() * 0.3;
    return { item: { x, z, radius, height: radius * (0.6 + rng() * 0.5) }, size: radius };
  });
  const pines = scatter(rng, 26, SHORE_RADIUS + 1.2, SHORE_RADIUS + 6, (x, z) => ({
    item: { x, z, height: 1.1 + rng() * 1.3 },
    size: 0.6,
  }));
  return { floes, banks, rocks, pines };
}
